import React, { useEffect, useState, useMemo } from "react";
import { useStore, StoreTypes } from "context";
import * as types from "constants/actionTypes";
import styles from "./index.module.scss";
import classnames from "classnames";
import { useFetchBooks } from "customHooks/book";
import DialogModule from "components/BookStore/ReferenceBookStore/Dialogs";
import Repository from "repositories/Repository";
import Card from "components/BookStore/ReferenceBookStore/Card";
import Pagination from "components/BookStore/ReferenceBookStore/Pagination";
import Autocomplete from "components/BookStore/ReferenceBookStore/Autocomplete";
import useSetState from "customHooks/setState";
import {
  useUpdateDisplayLanguage,
  useUpdateUserSettings,
  useUpdateUserFavorites,
} from "customHooks/userSetting";
import { UserSettingsAPI } from "api/UserSettingsAPI";
import Header from "components/BookStore/ReferenceBookStore/Header";
import Footer from "components/BookStore/ReferenceBookStore/Footer";
import LoginPopup from "components/Login/Popup";
import { LOGIN_POPUP } from "constants/loginTypes";
import { scrollTo } from "util/scrollTo";
import {
  subjectTable,
  categoryTable
} from "constants/optionTable";
import Loading from "components/BookStore/ReferenceBookStore/Loading";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

export const excludedTags = {
	Grade: ['四年級', '五年級', '六年級', '七年級', '八年級', '九年級','低年級', '中年級', '高年級'],
	EducationalSystem: ['國小', '高中'],
	Subject: ['國語', '生活','健體', '藝文', '綜合', '綜合活動', '健康與體育', '社會', '科技', '物理', '化學', '自然與生活科技', '英聽', '公民與社會', '英文', '地球科學'],
}

export const ReferenceBookStore = () => {
  const [, readerDispatch] = useStore(StoreTypes.reader);
  const [{ bookList }] = useStore(StoreTypes.books);
  const [{ token, isLogin }] = useStore(StoreTypes.user);
  const [, loginDispatch] = useStore(StoreTypes.login);
  const [, annotationDispatch] = useStore(StoreTypes.annotation);
  const { isLoading, getBookList, getBookTags } = useFetchBooks();
  const { BookContentRepository } = Repository;
  const updateUserSettings = useUpdateUserSettings();
  const updateUserFavorites = useUpdateUserFavorites();
  // tag 資料
  const [bookTags, setBookTags] = useState({});
  const [{ books }] = useStore(StoreTypes.settings);
  const [{ SchoolYear, EducationalSystem, Subject, Grade, Category }, setTag] =
    useSetState({
      SchoolYear: [],
      EducationalSystem: [],
      Subject: [],
      Grade: [],
      Category: [],
    });

  const [addShelfDialog, setShelfDialog] = useState({
    state: false,
    content: "",
  });
  const [pageState, setPageState] = useState({
    currentPage: 1,
  });
  // tag 初始選項
  const [initTag, setInitTag] = useState({});
  // 篩選 tag
  const [filterTag, setFilterTag] = useState([]);
  // 篩選 tag 順序
  const [filterOrderTag, setFilterOrderTag] = useState([]);
  const root = document.getElementById("root");

  useEffect(() => {
    if (!isLogin) return;
    (async () => {
      //const userSettings = await UserSettingsAPI.getUserSettings(token);
      const favorites = await UserSettingsAPI.getUserFavorites(token);

      // if (userSettings.status === 'success') {
      //     await updateUserSettings(userSettings.data, false);
      // }

      if (favorites.status === "success") {
        await updateUserFavorites(favorites.data, false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin, updateUserSettings]);

  useEffect(() => {
    readerDispatch({ type: types.SET_BOOK_PAGE_INDEX, pageIndex: 0 });
    annotationDispatch({ type: types.RESET_ANNOTATION_INFO });
  }, [annotationDispatch, readerDispatch]);

  useEffect(() => {
    (async () => {
      const tags = await getBookTags();
      const dataMap = {
        Grade: tags.filter((tag) => tag.category === "Grade").filter(tag => excludedTags.Grade.indexOf(tag.label) === -1 && tag.label.includes('年級')),
        Subject: tags.filter((tag) => tag.category === "Subject").filter(tag => excludedTags.Subject.indexOf(tag.label) === -1),
        EducationalSystem: tags.filter(
          (tag) => tag.category === "EducationalSystem"
        ).filter(tag => excludedTags.EducationalSystem.indexOf(tag.label) === -1),
        SchoolYear: tags.filter((tag) => tag.category === "SchoolYear"),
				Category: tags.filter(tag => tag.category === 'Grade' && !tag.label.includes('年級')).map(tag => ({...tag, category: 'Category'})),
      };

      setInitTag(dataMap);
      setBookTags(dataMap);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changePage = (event, page) => {
    (async () => {
      const tagData = [
        ...SchoolYear,
        ...EducationalSystem,
        ...Subject,
        ...Grade,
        ...Category
      ];
      setFilterTag(tagData);
      await getBookList(page, tagData);
      scrollTo(root, 0, 300);
      setPageState({ currentPage: page });
    })();
  };

  const pageCount = useMemo(() => {
    if (bookList.total > 0) {
      return Math.ceil(bookList.total / 10);
    }
    return 0;
  }, [bookList.total]);

  const filterChange = (event, value, key) => {
    let tagArr = [];
    const optionObj = value;
    const category = key;

    if (optionObj === null) {
      const deleteOrderTagIndex = filterOrderTag.findIndex((item) => {
        return item.category === category;
      });

      if (deleteOrderTagIndex === -1) {
        setTag({ [category]: tagArr });
        return;
      }

      const updateOrderArr = [...filterOrderTag];

      updateOrderArr.splice(deleteOrderTagIndex, 1);
      setFilterOrderTag([...updateOrderArr]);
    } else {
      const isSkip =
        optionObj.category !== "SchoolYear" &&
        optionObj.label !== "低年級" &&
        optionObj.label !== "中年級" &&
        optionObj.label !== "高年級";
      tagArr.push(optionObj);

      if (filterOrderTag.length > 0 && isSkip) {
        const someIndex = filterOrderTag.findIndex(
          (item) => optionObj.category === item.category
        );
        if (someIndex !== -1) {
          const updateOrderArr = [...filterOrderTag];
          updateOrderArr[someIndex] = optionObj;
          setFilterOrderTag([...updateOrderArr]);
        } else {
          setFilterOrderTag([...filterOrderTag, ...tagArr]);
        }
      } else if (isSkip) {
        setFilterOrderTag([...filterOrderTag, ...tagArr]);
      }
    }
    setTag({ [category]: tagArr });
    setPageState({ currentPage: 1 });
  };

  // 篩選 options
  const filterCategoryOptions = (tableType, options) => {
    let categoryTag;
    let subjectTag;
    if (
      tableType === "Subject" ||
      options.find((item) => item.category === "Subject")
    ) {
      const subjectOption = options.find((item) => item.category === "Subject");
      categoryTag = getOptionsData(
        "Category",
        subjectTable[subjectOption.label]? subjectTable[subjectOption.label].Category : initTag.Category.map(item => item.label)
      );
    }

    if (
      tableType === "Category" ||
      options.find((item) => item.category === "Category")
    ) {
      const categoryOption = options.find(
        (item) => item.category === "Category"
      );
      subjectTag = getOptionsData(
        "Subject",
        categoryTable[categoryOption.label]? categoryTable[categoryOption.label].Subject : initTag.Subject.map(item => item.label)
      );
    }

    setBookTags({
      ...initTag,
      SchoolYear: initTag.SchoolYear,
      ...(categoryTag && { Category: categoryTag }),
      ...(subjectTag && { Subject: subjectTag }),
    });
  };

  // 取得 option 資訊
  const getOptionsData = (key, optionArr) => {
    const newOptions = initTag[key].filter((item) => {
      return optionArr.find((option) => {
        return option === item.label;
      });
    });
    return newOptions;
  };

  const addShelf = (event, bookData) => {
    event.stopPropagation();
    if (!token) {
      loginDispatch({
        type: LOGIN_POPUP,
        popupState: true,
      });
      // alertShelfDialog(
      //   <>
      //     <div className={classnames(styles.referenceDialog, styles.warning)}>
      //       <div className={styles.icon}>
      //         <WarningIcon />
      //       </div>
      //       請先登入
      //     </div>
      //   </>
      // );
      return;
    }

    const isBookIdExist = books.hasOwnProperty(bookData.productItemId);

    if (isBookIdExist) {
      alertShelfDialog(<>
				<div className={classnames(styles.referenceDialog, styles.warning)}>
					<div className={styles.icon}>
						<WarningIcon />
					</div>
					此書已經在書櫃裡囉！
				</div>
			</>);
      return;
    }

    const currentTime = Date.now();
    const shelfBookObj = {};

    shelfBookObj[bookData.productItemId] = {
      collectedTime: currentTime,
      bookId: bookData.bookId,
      id: bookData.productItemId,
    };

    updateUserFavorites({ books: { ...books, ...shelfBookObj } });
    alertShelfDialog(<>
			<div className={classnames(styles.referenceDialog, styles.success)}>
				<div className={styles.icon}>
					<CheckCircleIcon />
				</div>
				您已成功將書本放入書櫃！
			</div>
		</>);
  };

  // 加入書櫃提示
  const alertShelfDialog = (content) => {
    setShelfDialog({ state: true, content: content });
    // setTimeout(() => {
    //   setShelfDialog({ state: false, content: content });
    // }, 800);
  };

  useEffect(() => {
    const isFilter = filterOrderTag.length > 0;

    if (!isFilter) {
      filterCategoryOptions("init", filterOrderTag);
    } else {
      let tableCategory = filterOrderTag[0].category;
      filterCategoryOptions(tableCategory, filterOrderTag);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOrderTag]);

  useEffect(() => {
    const tagData = [...SchoolYear, ...EducationalSystem, ...Subject, ...Grade, ...Category];
    setFilterTag(tagData);
    getBookList(1, tagData);
    scrollTo(root, 0, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SchoolYear, EducationalSystem, Subject, Grade, Category]);

  return (
    <>
      <Header path={"輔材書城"} />
      <LoginPopup />
      <div className={styles.container}>
        <div className={classnames(styles.decoration, styles.tree)} />
        <div className={classnames(styles.decoration, styles.kite)} />
        <div className={styles.filter}>
          <div className={styles.filterContainer}>
            <Autocomplete
              title={"學制"}
              optionsData={bookTags.EducationalSystem}
              onChange={(event, value) =>
                filterChange(event, value, "EducationalSystem")
              }
              choosed={filterOrderTag.find(tag => tag.category === "EducationalSystem")}
            />
            <Autocomplete
              title={"科目"}
              optionsData={bookTags.Subject}
              onChange={(event, value) => filterChange(event, value, "Subject")}
              choosed={filterOrderTag.find(tag => tag.category === "Subject")}
            />
            <Autocomplete
              title={"學年度"}
              optionsData={bookTags.SchoolYear}
              onChange={(event, value) =>
                filterChange(event, value, "SchoolYear")
              }
              choosed={filterOrderTag.find(tag => tag.category === "SchoolYear")}
            />
            <Autocomplete
              title={"類別"}
              optionsData={bookTags.Category}
              onChange={(event, value) => filterChange(event, value, "Category")}
              choosed={filterOrderTag.find(tag => tag.category === "Category")}
            />
            <Autocomplete
              title={"年級"}
              optionsData={bookTags.Grade}
              onChange={(event, value) => filterChange(event, value, "Grade")}
              choosed={filterOrderTag.find(tag => tag.category === "Grade")}
            />
          </div>
        </div>
        <div className={styles.bookCardContainer}>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              {bookList.lists.length > 0 && (
                <ul className={styles.listContainer}>
                  {bookList.lists.map((book) => {
                    return (
                      <Card
                        book={book}
                        BookContentRepository={BookContentRepository}
                        addShelf={addShelf}
                        key={book.bookId}
                      />
                    );
                  })}
                </ul>
              )}
              {bookList.lists.length === 0 && filterTag.length !== 0 && (
                <div className={styles.noResult}>
                  <img
                    src="/img/referenceBookNoResult.svg"
                    alt="找不到符合搜尋條件的書本"
                  />
                  找不到符合搜尋條件的書本
                </div>
              )}
              <DialogModule
                open={addShelfDialog.state}
                content={addShelfDialog.content}
                setOpen={setShelfDialog}
              />
              {bookList.total > 0 ? (
                <Pagination
                  count={pageCount}
                  page={pageState.currentPage}
                  onChange={changePage}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};
